import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import {
  getMyPermissions,
  type FirmCapability,
  type MyPermissions
} from "@/api/permissions";
import { queryClient } from "@/lib/query/queryClient";

import { useGetUserDetails } from "./useGetUserDetails";

const ADMIN_ROLES = new Set(["owner", "admin", "superadmin"]);

const ADMIN_ONLY: FirmCapability[] = [
  "edit_company_playbook",
  "manage_firm_settings",
  "manage_permissions",
  "review_billing"
];

/** Switched per role in Settings > Permissions; on until the firm turns one off. */
const SWITCHED: FirmCapability[] = ["manage_skills", "edit_firm_library"];

/**
 * What the lawyer's role allows before the server answers: owners and
 * admins everything; anyone else the switched capabilities (their default)
 * and none of the admin ones.
 */
export const roleCapabilities = (
  role: string | null | undefined
): Record<FirmCapability, boolean> => {
  const admin = ADMIN_ROLES.has(String(role ?? "").toLowerCase());
  return Object.fromEntries([
    ...ADMIN_ONLY.map((cap) => [cap, admin]),
    ...SWITCHED.map((cap) => [cap, true])
  ]) as Record<FirmCapability, boolean>;
};

export const firmPermissionsKey = (uid: string | undefined) =>
  ["me", "permissions", uid ?? ""] as const;

/**
 * The signed-in member's firm permissions: `can("edit_company_playbook")`.
 * The server's map (`GET /api/me/permissions`, the same one the API and the
 * case agent enforce) wins; until it lands, the lawyer's role decides.
 */
export const useFirmPermissions = () => {
  const user = useGetUserDetails().userDetails;
  const isLawyer = user.userType === "Lawyer";
  const query = useQuery<MyPermissions>(
    {
      queryKey: firmPermissionsKey(user.id),
      queryFn: getMyPermissions,
      staleTime: 5 * 60_000,
      enabled: isLawyer && Boolean(user.id)
    },
    queryClient
  );
  const capabilities = useMemo(
    () => query.data?.capabilities ?? roleCapabilities(user.role),
    [query.data, user.role]
  );
  const can = useCallback(
    (capability: FirmCapability) =>
      isLawyer && Boolean(capabilities[capability]),
    [capabilities, isLawyer]
  );
  return {
    can,
    role: query.data?.role ?? user.role ?? null,
    isLoading: query.isLoading
  };
};
